import { Component } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../../services/alerts.service';
import { UserService } from '../../../services/user.service';
import { BatchService } from '../../services/batch.service';
import { BatchManagementComponent } from '../../components/batch-management/batch-management.component';
import { BatchPropertiesComponent } from '../../components/batch-properties/batch-properties.component';
import { BatchDocumentsComponent } from '../../components/batch-documents/batch-documents.component';
import { BatchQueriesAgComponent } from '../../components/batch-queries-ag/batch-queries-ag.component';
import { LogsComponent } from '../../components/logs/logs.component';

/**
 * Main workflow batch management component.
 */
@Component({
	selector: 'workflow-batches',
	templateUrl: './batches.component.html',
	styleUrl: './batches.component.css'
})
export class BatchesComponent implements OnInit, OnDestroy {

	private batchEventSubscription!: Subscription;
	private userServiceSubscription!: Subscription;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	inprogress: boolean = false;

	batches: any[] = [];
	selectedBatches = {
		all: false,
		selected: 0,
		canRetry: false,
		canAbort: false,
		canDelete: false
	};

	filters = [
		{ name: '', label: '' },
		{ name: 'name', label: 'Batch' },
		{ name: 'state', label: 'Status' },
		{ name: 'priority', label: 'Priority' },
		{ name: 'workflowType', label: 'Workflow' }
		//		{ name: 'parametersLike', label: 'Worflow Parameters', isMetadata: true } // Deprecated: OCSC
	];
	filter = {
		active: this.filters[0],
		value: ''
	};
	filterOpenState: boolean = true;

	constraints: any = {};
	pagination = {
		page: 1,
		elementsPerPage: 10,
		elementsPerPageSizes: [10, 20, 50],
		sorting: {
			sort: 'creationDate,desc',
		},
		totalElements: 0,
		pageGap: 1,
		resultTotal: 0,
		pageChanged: (newPage: any) => {
			this.pagination.page = newPage;
			this.loadBatches(this.pagination.page);
		}
	};
	private isSortDirAsc: boolean = true;

	constructor(
		private sanitizer: DomSanitizer,
		private ngbModal: NgbModal,
		private userService: UserService,
		private alertsService: AlertsService,
		private batchService: BatchService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
	}

	ngOnInit(): void {
		this.batchEventSubscription = this.batchService.workflowBatchEvent.subscribe(event => {
			console.log('Event received:', event);
			this.loadBatches();
		});
		
		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
			if ("localizationLoaded" === eventId) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			} else if ("userPrefsUpdate" === eventId) {
				this.userPermissions = this.userService.getUserPermissionsMap();
			}
		});			

		this.loadBatches();
	}

	ngOnDestroy() {
		if (this.batchEventSubscription) {
			this.batchEventSubscription.unsubscribe();
		}
		if (this.userServiceSubscription) {
			this.userServiceSubscription.unsubscribe();
		}		
	}

	trustHtml(html: string | null): SafeHtml {
		return this.sanitizer.bypassSecurityTrustHtml(html || '');
	}

	/**
	 * Loads the batches using the current constraints.
	 */
	loadBatches(page?: number): void {
		this.inprogress = true;

		if (page) {
			this.pagination.page = page;
		}

		let index = this.pagination.page - 1;

		this.batchService.getBatches(index, this.pagination, this.constraints).then(
			(data: any) => {
				if (!data) {
					this.batches = [];
				} else {
					this.batches = data.content;
					this.pagination.page = data.page.number + 1;
					this.pagination.elementsPerPage = data.page.size;
					this.pagination.totalElements = data.page.totalElements;
					this.pagination.pageGap = 1 + (data.page.size * data.page.number);
					this.pagination.resultTotal = this.batches.length + (data.page.size * data.page.number);
				}
				this.inprogress = false;
				this.updateBatchStatus();
				this.decorateBatchProperties();
			},
			(err: any) => {
				console.error("Failed to fetch batches", err);
				this.batches = [];
				this.inprogress = false;
				this.updateBatchStatus();
			}
		);
	}

	clearFilter(): void {
		this.constraints = {};
		this.filter.active = this.filters[0];
		this.filter.value = '';
		this.applyFilter();
	}

	applyFilter(event?: Event): void {
		this.constraints = {};
		if (this.filter.active.name) {
			this.constraints[this.filter.active.name] = this.filter.value;
		}

		// Deprecated: OCSC
		//		if (this.filter.active.isMetadata) {
		//			var cvalue = this.value;
		//			if (!cvalue.startsWith('%')) {
		//				cvalue = '%' + cvalue;
		//			}
		//			if (!cvalue.endsWith('%')) {
		//				cvalue = cvalue + '%';
		//			}
		//			constraints['parametersLike'] = cvalue;
		//		}

		this.loadBatches();
	}

	pageSizeChanged(size: number): void {
		this.pagination.elementsPerPage = size;
		this.loadBatches(1);
	}

	sortingChanged(sortby: string): void {
		this.isSortDirAsc = !this.isSortDirAsc;
		let direction = this.isSortDirAsc ? 'asc' : 'desc';
		this.pagination.sorting = { sort: sortby + ',' + direction };
		this.loadBatches(1);
	}

	private updateBatchStatus(args?: any): void {
		this.selectedBatches = {
			all: false,
			selected: 0,
			canRetry: false,
			canAbort: false,
			canDelete: false
		};

		let allCanAbort = true;
		let allCanRetry = true;
		let allCanDelete = true;

		this.batches.forEach((batch: any) => {
			if (args && args.hasOwnProperty('allToggled')) batch.selected = args.allToggled;

			if (batch.selected) {
				this.selectedBatches.selected = this.selectedBatches.selected + 1;

				if (!batch.actions.canAbort) allCanAbort = false;
				if (!batch.actions.canAssign) allCanRetry = false;
				if (!batch.actions.canDelete) allCanDelete = false;
			}
		});

		if (this.selectedBatches.selected > 0) {
			this.selectedBatches.canAbort = allCanAbort;
			this.selectedBatches.canRetry = allCanRetry;
			this.selectedBatches.canDelete = allCanDelete;
		}
	}

	toggleAllBatches(): void {
		let toggleStatus = !this.selectedBatches.all;
		this.batches.forEach((batch: any) => {
			batch.selected = toggleStatus;
		});
		this.updateBatchStatus({ allToggled: toggleStatus });
	}

	toggleBatchId(id: number): void {
		this.selectedBatches.all = this.batches.every((b: any) => b.selected);
		this.updateBatchStatus();
	}

	/**
	 * Aborts the workflow task processes of the chosen batches.
	 */
	onAbort(id?: number): void {
		const modalRef = this.ngbModal.open(BatchManagementComponent);

		let batchids: number[] = [];
		if (id) {
			batchids = [id];
		} else {
			for (let i = 0, len = this.batches.length; i < len; i++) {
				let b = this.batches[i];
				if (b.selected) { batchids.push(b.batchId); }
			}
		}

		modalRef.componentInstance.batchGroup = {
			batchids: batchids,
			mode: 'abort',
			comment: null
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Deletes the chosen batches.
	 */
	onDelete(id?: number): void {
		const modalRef = this.ngbModal.open(BatchManagementComponent);

		let batchids: number[] = [];
		if (id) {
			batchids = [id];
		} else {
			for (let i = 0, len = this.batches.length; i < len; i++) {
				let b = this.batches[i];
				if (b.selected) { batchids.push(b.batchId); }
			}
		}

		modalRef.componentInstance.batchGroup = {
			batchids: batchids,
			mode: 'delete',
			comment: null
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Retries starting the processing for the chosen batches.
	 */
	onRetry(id?: number): void {
		let payload = {
			batchIds: [] as number[]
		};

		if (id) {
			payload.batchIds = [id];
		} else {
			for (let i = 0, len = this.batches.length; i < len; i++) {
				let b = this.batches[i];
				if (b.selected) { payload.batchIds.push(b.batchId); }
			}
		}

		this.batchService.massRetryBatches(payload).then(
			(response: any) => {
				this.alertsService.success({
					message: this.LOCALIZATION.batchRestartSuccess,
					delay: 5000
				});
			},
			(err: any) => {
				console.error("Failed to release tasks", err);
				this.alertsService.error({
					message: this.LOCALIZATION.batchRestartError,
					delay: 5000
				});
			}
		);
	}

	/**
	 * Displays batch properties.
	 */
	showBatchProperties(batch: any): void {
		const modalRef = this.ngbModal.open(BatchPropertiesComponent);

		modalRef.componentInstance.batch = batch;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays batch document properties.
	 */
	showDocumentsProperties(batch: any): void {
		const modalRef = this.ngbModal.open(BatchDocumentsComponent);

		modalRef.componentInstance.batchId = batch.batchId;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays workflow queries assigned to the batch.
	 */
	showQueriesProperties(batch: any): void {
		const modalRef = this.ngbModal.open(BatchQueriesAgComponent);

		modalRef.componentInstance.batch = batch;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays logs for the batch.
	 */
	showBatchLogs(batch: any): void {
		const modalRef = this.ngbModal.open(LogsComponent);

		modalRef.componentInstance.args = {
			title: "Batch Logs",
			subtitle: batch.batchName,
			id: batch.batchId,
			type: "batch"
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	downloadResults(id: number, batchname: string): void {
		this.alertsService.info({
			title: this.LOCALIZATION.downloadStarted,
			message: this.LOCALIZATION.processingWaitMessage
		});

		this.batchService.downloadBatchResults(id).then(
			(response: any) => {
				let blob = new Blob([response], { type: "application/octet-stream" });
				let downloadName = `${batchname}.zip`;

				let link = document.createElement('a');
				link.style.display = 'none';
				let url = window.URL.createObjectURL(blob);
				link.href = url;
				link.download = downloadName;
				document.body.appendChild(link);
				link.click();

				setTimeout(() => {
					document.body.removeChild(link);
					window.URL.revokeObjectURL(url);
				}, 100);
			},
			(err: any) => {
				console.error("Failed to download results", err);
				var statustext = err.statusText || "Server error";
				if (err.status && err.status == 404) {
					this.alertsService.info({
						title: statustext,
						message: this.LOCALIZATION.resultsEmpty,
						delay: 5000
					});
				} else {
					this.alertsService.error({
						title: statustext,
						message: this.LOCALIZATION.downloadError,
						delay: 5000
					});
				}
			}
		);
	}
	
	// Custom (OCSC)
	private decorateBatchProperties(): void {
		//		var decorators = this.customize.propertiesDecorator || [];
		//		if (decorators.length == 0) return;
		//
		//		angular.forEach(this.batches, function (batch) {
		//			for (var i=0; i<decorators.length; i++) {
		//				var decorator = decorators[i];
		//				var fieldval = batch[decorator.field];
		//				if (fieldval) {
		//					var matches = fieldval.match(decorator.pattern);
		//					if (matches && matches[1]) {
		//						var decorated = fieldval.replace(matches[1], '<span class="' + decorator.clazz + '">' + matches[1] + '</span>')
		//						batch[decorator.field + "_decorated"] = decorated;
		//					}
		//				}
		//			}
		//		});
	}	

	// Deprecated (AFDO Air Force pilot)
	//		this.showBatchStatistics = function (batch) {
	//			$rootScope.$broadcast("closeModal");
	//
	//			var modalInstance = $uibModal.open({
	//				animation: this.animationsEnabled,
	//				templateUrl: '~/../app/js/workflow/statistics.html',
	//				windowClass: 'dx-modal dx-large-modal-window',
	//				controller: 'StatisticsCtrl',
	//				resolve: {
	//					args: function () {
	//						return {
	//							title: "Batch Result Statistics",
	//							subtitle: batch.batchName,
	//							total: batch.documents.length,
	//							id: batch.batchId,
	//							type: "batch" // batch, document
	//						}
	//					}
	//				}
	//			});
	//		}

	// Deprecated (AFDO Air Force pilot)
	//		this.downloadBatchStatistics = function (batch) {
	//        	alertsService.info({
	//				title: this.LOCALIZATION.downloadStarted,
	//				message: this.LOCALIZATION.processingWaitMessage
	//        	});
	//
	//			dxReportsService.getTaskStatistics(batch.batchId, 'batch').then(function (stats) {
	//				if (stats) {
	//					var content = dxReportsService.formatTaskStatisticsAsCSV(stats);
	//					var blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
	//					var downloadName = "{0}-{1}.{2}".format(batch.batchName, 'statistics', 'csv');
	//
	//					if (navigator.msSaveOrOpenBlob) {
	//						// IE support
	//						navigator.msSaveOrOpenBlob(blob, downloadName);
	//					} else {
	//						var pom = document.createElement('a');
	//						var url = URL.createObjectURL(blob);
	//						pom.href = url;
	//						pom.setAttribute('download', downloadName);
	//						document.body.appendChild(pom);
	//						pom.click();
	//						setTimeout(function () {
	//							document.body.removeChild(pom);
	//							window.URL.revokeObjectURL(url);
	//						}, 100);
	//					}
	//				}
	//			}, function () {
	//            	this.alertsService.error({
	//					message: this.LOCALIZATION.downloadError,
	//            		delay: 5000
	//            	});
	//			});
	//		}
}
